import { useRef } from 'react';
import { MdClose } from 'react-icons/md';
import type { ColumnPart, CustomColumn } from '../../customColumnTypes';
import { getColumnField, validateCustomColumn } from '../../customColumns';

export const fieldDragType = 'application/x-smartsell-order-field';

type Props = {
  definition: CustomColumn;
  onChange: (parts: ColumnPart[]) => void;
  onError: (message: string) => void;
  insertRef: React.MutableRefObject<((path: string) => void) | null>;
};

// Keep an editable text slot on each side of every card. React owns the entire
// document, including pasted text; HTML and field paths are never evaluated.
export default function ColumnValueEditor({ definition, onChange, onError, insertRef }: Props) {
  const inputs = useRef<(HTMLInputElement | null)[]>([]);
  const active = useRef(0);
  const parts = definition.parts;

  const commit = (next: ColumnPart[], focus?: { index: number; offset: number }) => {
    const error = validateCustomColumn({ ...definition, parts: next }, true);
    if (error) {
      onError(error);
      return;
    }
    onError('');
    onChange(next);
    if (focus) requestAnimationFrame(() => {
      active.current = focus.index;
      const input = inputs.current[focus.index];
      input?.focus();
      input?.setSelectionRange(focus.offset, focus.offset);
    });
  };

  const insert = (path: string, at = active.current) => {
    const field = getColumnField(path);
    if (!field || (definition.type === 'number' && !field.numeric)) {
      onError('Escolha um campo numérico para este cálculo.');
      return;
    }
    const index = parts[at]?.type === 'text' ? at : parts.length - 1;
    const slot = parts[index];
    if (!slot || slot.type !== 'text') return;
    const input = inputs.current[index];
    const start = input?.selectionStart ?? slot.value.length;
    const end = input?.selectionEnd ?? start;
    commit([
      ...parts.slice(0, index),
      { type: 'text', value: slot.value.slice(0, start) },
      { type: 'field', path },
      { type: 'text', value: slot.value.slice(end) },
      ...parts.slice(index + 1),
    ], { index: index + 2, offset: 0 });
  };
  insertRef.current = insert;

  const remove = (index: number) => {
    const left = parts[index - 1];
    const right = parts[index + 1];
    if (left?.type !== 'text' || right?.type !== 'text') return;
    // Removal is always possible, even if it leaves a temporarily incomplete calculation.
    onError('');
    onChange([
      ...parts.slice(0, index - 1),
      { type: 'text', value: left.value + right.value },
      ...parts.slice(index + 2),
    ]);
    requestAnimationFrame(() => {
      active.current = index - 1;
      inputs.current[index - 1]?.focus();
      inputs.current[index - 1]?.setSelectionRange(left.value.length, left.value.length);
    });
  };

  return (
    <div className="value-editor" role="group" aria-label="Valor da coluna"
      onDragOver={(event) => { event.preventDefault(); event.dataTransfer.dropEffect = 'copy'; }}
      onDrop={(event) => {
        event.preventDefault();
        const path = event.dataTransfer.getData(fieldDragType);
        if (path) insert(path);
      }}>
      {parts.map((part, index) => part.type === 'field' ? (
        <span className="field-card" key={index}>
          {getColumnField(part.path)?.entity} · {getColumnField(part.path)?.label}
          <button type="button" aria-label={`Remover ${getColumnField(part.path)?.label}`}
            onClick={() => remove(index)}><MdClose /></button>
        </span>
      ) : (
        <input key={index} ref={(node) => { inputs.current[index] = node; }}
          className="value-text" aria-label={`Texto ou número na posição ${index / 2 + 1}`}
          inputMode={definition.type === 'number' ? 'decimal' : 'text'}
          value={part.value} maxLength={2000} autoComplete="off"
          placeholder={parts.length === 1 ? 'Arraste um campo ou digite aqui…' : ''}
          style={{ width: parts.length === 1 ? '100%' : `${Math.max(part.value.length + 1, 3)}ch` }}
          onFocus={() => { active.current = index; }}
          onDrop={(event) => {
            event.preventDefault(); event.stopPropagation();
            const path = event.dataTransfer.getData(fieldDragType);
            if (path) insert(path, index);
          }}
          onKeyDown={(event) => {
            if (event.key === 'Backspace' && event.currentTarget.selectionStart === 0
              && event.currentTarget.selectionEnd === 0 && index > 0) {
              event.preventDefault(); remove(index - 1);
            } else if (event.key === 'Delete' && event.currentTarget.selectionStart === part.value.length
              && event.currentTarget.selectionEnd === part.value.length && index < parts.length - 1) {
              event.preventDefault(); remove(index + 1);
            }
          }}
          onChange={(event) => commit(parts.map((item, i) => i === index
            ? { type: 'text', value: event.target.value } : item))} />
      ))}
    </div>
  );
}
